import { ControlsLike, GameSnapshot, Rect } from "./types";

export interface AutopilotOptions {
  /** Seconds between decisions. A human-ish delay keeps it from looking robotic. */
  reactionTime?: number;
  /** How far above the player an invader bullet starts to count as a threat. */
  threatDistance?: number;
  /** Alignment slack, in px, before it takes the shot. */
  aimTolerance?: number;
  /** How close to a wall it can be pushed before it dodges the other way. */
  wallMargin?: number;
}

const centreOf = (rect: Rect) => rect.x + rect.width / 2;

/**
 * Drives the hero's demo run. It is a pure function of a snapshot, so it can be
 * unit-tested without a canvas and swapped for real Controls at any moment.
 */
export class Autopilot implements ControlsLike {
  left = false;
  right = false;
  fire = false;

  private timer = 0;
  private readonly reactionTime: number;
  private readonly threatDistance: number;
  private readonly aimTolerance: number;
  private readonly wallMargin: number;

  constructor(options: AutopilotOptions = {}) {
    this.reactionTime = options.reactionTime ?? 0.12;
    this.threatDistance = options.threatDistance ?? 260;
    this.aimTolerance = options.aimTolerance ?? 6;
    this.wallMargin = options.wallMargin ?? 40;
  }

  update(dt: number, provide: () => GameSnapshot): void {
    this.timer -= dt;
    if (this.timer > 0) return; // hold the last decision
    this.timer = this.reactionTime;

    const snap = provide();
    const centre = centreOf(snap.player);

    // Hold the trigger down. The word is a dense block spanning most of the
    // canvas, so a shot going up is almost always productive, and gating fire
    // on being perfectly aligned made a loop take minutes rather than seconds.
    this.fire = snap.invaders.length > 0;

    const threat = this.nearestThreat(snap, centre);
    if (threat) {
      this.dodge(snap, centre, threat);
      return;
    }

    const target = this.bestTarget(snap, centre);
    if (!target) {
      this.left = false;
      this.right = false;
      return;
    }

    const delta = centreOf(target) - centre;
    this.left = delta < -this.aimTolerance;
    this.right = delta > this.aimTolerance;
  }

  /** The closest invader bullet already lined up with the player's column. */
  private nearestThreat(snap: GameSnapshot, centre: number): Rect | null {
    let best: Rect | null = null;
    for (const bullet of snap.invaderBullets) {
      const overlapsColumn =
        bullet.x < snap.player.x + snap.player.width + 8 &&
        bullet.x + bullet.width > snap.player.x - 8;
      const closing =
        bullet.y < snap.player.y && bullet.y > snap.player.y - this.threatDistance;
      if (!overlapsColumn || !closing) continue;
      if (!best || bullet.y > best.y) best = bullet;
    }
    if (best) return best;

    // Nothing in the column yet — but a bullet drifting into it still counts.
    return (
      snap.invaderBullets.find(
        (b) =>
          Math.abs(centreOf(b) - centre) < snap.player.width &&
          b.y > snap.player.y - this.threatDistance
      ) ?? null
    );
  }

  private dodge(snap: GameSnapshot, centre: number, threat: Rect): void {
    let dir = centre <= centreOf(threat) ? -1 : 1;
    if (dir < 0 && snap.player.x < this.wallMargin) dir = 1;
    if (
      dir > 0 &&
      snap.player.x + snap.player.width > snap.canvasWidth - this.wallMargin
    ) {
      dir = -1;
    }
    this.left = dir < 0;
    this.right = dir > 0;
  }

  /** Low invaders threaten soonest; near ones cost less travel. */
  private bestTarget(snap: GameSnapshot, centre: number): Rect | null {
    let best: Rect | null = null;
    let bestScore = Infinity;
    for (const invader of snap.invaders) {
      const score = Math.abs(centreOf(invader) - centre) - invader.y * 2;
      if (score < bestScore) {
        bestScore = score;
        best = invader;
      }
    }
    return best;
  }
}
